import { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Camera, Loader2, ChevronRight } from 'lucide-react';
import SettingCard from '../../components/settings/SettingCard';
import SaveBar from '../../components/settings/SaveBar';
import { SETTINGS_NAV } from '../../components/settings/settingsNav';
import { useSettingsStore } from '../../store/settingsStore';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import type { UserProfile } from '../../types/settings.types';

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all text-sm';

export default function SettingsHome() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { data, loading, saving, saveProfile } = useSettingsStore();
  const [form, setForm] = useState<UserProfile | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the editable form from the store once it hydrates. Adjusting state
  // during render (guarded so it runs once) is React's recommended pattern for
  // deriving local state from an async source — no effect needed.
  if (data && form === null) {
    setForm(data.profile);
  }

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-400 dark:text-neutral-500">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const set = <K extends keyof UserProfile>(key: K, value: UserProfile[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setSaved(false);
  };

  const onSave = async () => {
    if (!form) return;
    await saveProfile(form);
    setSaved(true);
  };

  const email = user?.email ?? form.email;
  const avatar = user?.photoURL || form.avatarUrl;

  return (
    <div className="space-y-6">
      <div className="hidden lg:block">
        <h1 className="text-2xl font-bold tracking-tight">{t('settings.profile.title')}</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t('settings.profile.subtitle')}</p>
      </div>

      <SettingCard>
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="relative w-24 h-24 rounded-3xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 shrink-0 group">
            <img src={avatar} alt={form.displayName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
              <Camera className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold truncate">{form.displayName || t('settings.profile.yourName')}</h2>
            <p className="text-neutral-500 dark:text-neutral-400 text-sm truncate">@{form.username} · {form.headline}</p>
            <p className="text-neutral-400 dark:text-neutral-500 text-xs mt-1 truncate">{email}</p>
          </div>
        </div>
      </SettingCard>

      <SettingCard title={t('settings.profile.personalInfo')} icon={User} description={t('settings.profile.personalInfoDesc')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('settings.profile.displayName')} value={form.displayName} onChange={(v) => set('displayName', v)} />
          <Field label={t('settings.profile.username')} value={form.username} onChange={(v) => set('username', v)} prefix="@" />
          <Field label={t('settings.profile.headline')} value={form.headline} onChange={(v) => set('headline', v)} />
          <Field label={t('settings.profile.location')} value={form.location} onChange={(v) => set('location', v)} />
          <Field label={t('settings.profile.phone')} value={form.phone} onChange={(v) => set('phone', v)} />
          <Field label={t('settings.profile.website')} value={form.website} onChange={(v) => set('website', v)} />
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-neutral-700">{t('settings.profile.bio')}</label>
            <textarea
              value={form.bio}
              onChange={(e) => set('bio', e.target.value)}
              rows={3}
              className={inputClass + ' mt-1 resize-none'}
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-5">
          <SaveBar saving={saving} saved={saved} />
          <button
            onClick={onSave}
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-60"
          >
            {saving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </div>
      </SettingCard>

      {/* Quick access to every category — especially useful on mobile where the rail is hidden. */}
      <SettingCard title={t('settings.profile.allSettings')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SETTINGS_NAV.filter((i) => !i.end).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="group flex items-center gap-3 rounded-2xl px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors"
            >
              <span className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300 group-hover:bg-neutral-900 group-hover:text-white transition-colors">
                <item.icon className="w-5 h-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-neutral-900 dark:text-neutral-50 truncate">{t(item.labelKey)}</span>
                <span className="block text-xs text-neutral-500 dark:text-neutral-400 truncate">{t(item.descriptionKey)}</span>
              </span>
              <ChevronRight className="w-5 h-5 text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-50" />
            </Link>
          ))}
        </div>
      </SettingCard>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  prefix,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  prefix?: string;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-neutral-700">{label}</label>
      <div className="relative mt-1">
        {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 text-sm">{prefix}</span>}
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass + (prefix ? ' pl-8' : '')}
        />
      </div>
    </div>
  );
}
